import { useCallback, useState } from 'react';
import isNil from 'lodash/isNil';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	performPostRequest as jsmPerformPostRequest,
	performPutRequest as jsmPerformPutRequest,
} from '@atlassian/jira-servicedesk-common/src/utils/fetch/requests.tsx';
import type { PayloadMetric } from '../../common/types';
import type { Props, State } from './types';
import { getUrl } from './utils';

export const useUpdateMetricService = ({ baseUrl, projectKey }: Props): State => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [loading, setLoading] = useState<boolean>(false);
	const [data, setData] = useState<unknown>(null);
	const [error, setError] = useState<unknown>(null);

	const update = useCallback(
		async (metric: PayloadMetric, id?: string) => {
			setLoading(true);
			setError(null);

			try {
				// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
				const metricId = parseInt(id, 10) || undefined;

				const asyncRequestAction = isNil(metricId) ? jsmPerformPostRequest : jsmPerformPutRequest;

				const payloadMetric = metricId ? { id: metricId, ...metric } : metric;

				const response = await asyncRequestAction(getUrl(baseUrl, projectKey, metricId), {
					body: JSON.stringify(payloadMetric),
				});

				const {
					config: {
						goals: newGoals,
						definition: { start, stop, pause },
					},
				} = response;

				setLoading(false);
				setData(response);
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`slaMetric ${isNil(metricId) ? 'created' : 'updated'}`,
					{
						statsPerSLA: {
							...(isNil(metricId) ? {} : { slaID: metricId }),
							numberOfGoals: newGoals.length,
							numberOfStartConditions: start.length,
							numberOfPauseConditions: pause.length,
							numberOfStopConditions: stop.length,
						},
					},
				);
				return response;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setLoading(false);
				setError(err);
				trackFetchErrors({
					error: err,
					id: 'useUpdateMetricService',
					packageName: 'jiraServicedeskSlaSettings',
					sendToPrivacyUnsafeSplunk: true,
				});
				return undefined;
			} finally {
				setLoading(false);
			}
		},
		[baseUrl, createAnalyticsEvent, projectKey],
	);

	return [
		{
			loading,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'ServiceError'.
			error,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'ServiceData<TimeMetric>'.
			data,
		},
		{
			update,
		},
	];
};
